import { AuthConfig } from 'angular-oauth2-oidc';
import { Type } from 'class-transformer';

export class BaseConfig {
  apiUrl: string = '';

  pageTitle: string = '';

  applicationName: string = 'embraceableAI Console';

  administrationTitle: string = $localize`Administration`;

  documentationUrl: string = '';

  usePasswordAuth: boolean = false;

  maxFileSize: number = 0;

  minPasswordLength: number = 8;

  @Type(() => AuthConfig)
  authConfig?: AuthConfig;

  keycloakConfig: KeycloakConfig;

  brandingConfig: BrandingConfig;

  featureFlags: FeatureFlags;

  chatConfig: ChatConfig;

  defaultSystemPromptTemplate: string =
    'Du bist der Dokumentenassistent. Du hilfst, Dokumente zu verstehen. Zu jeder Frage erhältst du \n Quellen, die mit "QUELLE Zahl:" gekennzeichnet sind. Der Nutzer kann eine Frage \n zu diesen Dokumenten stellen. Falls er keine Frage stellt, bitte den Nutzer eine Frage zu stellen. Die Anfrage des Nutzers wird mit ANFRAGE gekennzeichnet. Beantworte \n die Anfrage anhand der Quellen. Deine Antwort wird immer mit "ANTWORT:" gekennzeichnet. \n Falls die Antwort nicht in den Quellen vorhanden \n ist, so musst du das dem Nutzer schreiben. Schreibe, welche der Quellen du \n genutzt hast. Wenn der Nutze eine List anfrägt, erstelle eine Liste. \n Schreibe hierzu am Ende "QUELLEN:". Beschränke deine Antwort auf 12 Sätze. Beispiel: \n  \n ANFRAGE: Wann wurde die Weltbank gegründet? Wo wurde sie gebaut? \n QUELLE 1: Die Weltbank wurde 1984 gegründet. \n QUELLE 2: Das Auto ist die beste Erfindung der Welt. \n QUELLE 3: Die Weltbank wurde in New York errichtet. \n ANTWORT: Die Weltbank wurde 1984 gegründet (QUELLE 1) und in New York gebaut (QUELLE 3). \n  \n QUELLEN: 1, 3 \n  \n  \n ANFRAGE: Nenne drei Beispiel für gutes Essen. \n QUELLE 1: Aus Italien kommt das gute Spaghetti sowie die Pizza. \n QUELLE 2: Aus Deutschland kommen die beliebten Gerichte Currywurst, sowie die Spätzle. \n QUELLE 3: Die Pizza besteht aus Teig, Tomatensoße und Käse. \n ANTWORT: Beispiele für gutes Essen sind: \n     - Spaghetti (QUELLE 1) \n     - Pizza (QUELLE 1) (QUELLE 3) \n     - Currywurst (QUELLE 2) \n     - Spätzle (QUELLE 2) \n  \n QUELLEN: 1, 2, 3 \n  \n ANFRAGE: Hallo! \n QUELLE 1: Unscheinbarer Text zu Paris \n ANTWORT: Hallo! Stelle mir gerne Frage! \n  \n QUELLEN: - \n  \n  \n ANFRAGE: In welchem Land ist London? \n QUELLE 1: Paris ist in Frankreich, Berlin in Deutschland. \n QUELLE 1: Spaghetti kommt aus Italien. \n ANTWORT: Aus den Quellen lässt sich die Frage nicht beantworten. \n  \n QUELLEN: - \n';

  groups: GroupsConfig;

  validatorConfig: ValidatorConfig;

  adminEmail: string = '';

  /**
   * validate ensures syntactical correctness of the config
   * @throws Error with description if validation fails
   */
  public validate(): boolean {
    if (!isString(this.apiUrl) || this.apiUrl.length === 0) {
      throw new Error(
        'API URL is required and must be a non-empty string',
      );
    }

    if (this.authConfig === undefined) {
      throw new Error('Auth config is required');
    }

    if (
      !this.usePasswordAuth &&
      this.authConfig.responseType !== 'code'
    ) {
      throw new Error(
        'Response type must be "code" when not using password authentication',
      );
    }

    if (!isNumber(this.maxFileSize)) {
      throw new Error('Max file size must be a number');
    }

    if (this.keycloakConfig === undefined) {
      throw new Error('Keycloak config is required');
    }

    if (
      !isString(this.keycloakConfig.realm) ||
      this.keycloakConfig.realm.length === 0
    ) {
      throw new Error(
        'Keycloak realm is required and must be a non-empty string',
      );
    }

    if (
      !isString(this.keycloakConfig.resourceServerId) ||
      this.keycloakConfig.resourceServerId.length === 0
    ) {
      throw new Error(
        'Keycloak resource server ID is required and must be a non-empty string',
      );
    }

    if (
      !isString(this.keycloakConfig.authServerUrl) ||
      this.keycloakConfig.authServerUrl.length === 0
    ) {
      throw new Error(
        'Keycloak auth server URL is required and must be a non-empty string',
      );
    }

    if (this.featureFlags === undefined) {
      throw new Error('Feature flags are required');
    }

    if (this.featureFlags.maintenanceCheck === undefined) {
      throw new Error('Maintenance check feature flag is required');
    }

    if (
      this.chatConfig === undefined ||
      !isString(this.chatConfig.baseUrl) ||
      this.chatConfig.baseUrl.length === 0
    ) {
      throw new Error(
        'Chat base URL is required and must be a non-empty string',
      );
    }

    return true;
  }
}

function isString(value: unknown): boolean {
  return typeof value === 'string';
}

function isNumber(value: unknown): boolean {
  return typeof value === 'number';
}

export interface KeycloakConfig {
  realm: string;
  resourceServerId: string;
  authServerUrl: string;
}

export interface BrandingConfig {
  logoDark: string;
  logoLight: string;
  stylesDark: string;
  stylesLight: string;
}

export interface ChatConfig {
  baseUrl: string;
}

export interface GroupsConfig {
  attribute: GroupAttribute[];
}

export interface GroupAttribute {
  name: string;
  displayName: string;
  description: string;
  optional: boolean;
}

interface FeatureFlags {
  maintenanceCheck: boolean;
}

interface ValidatorConfig {
  [field: string]: string | boolean | number;
}
